import type { CSSProperties } from "react";
import type { DayPickerProps } from "react-day-picker";
import { enUS, es, ptBR } from "react-day-picker/locale";

import "react-day-picker/dist/style.css";

/**
 * Shared react-day-picker setup for `ColocaDateField` and `ColocaDateRangeField` — one source of truth for
 * locale resolution, ISO `YYYY-MM-DD` conversion, and the Coloca visual theme, so both calendars stay
 * pixel-identical. The shared `Chevron` component lives in `DayPickerChevron.tsx` (react-refresh requires
 * component-only files).
 */

export function resolveDayPickerLocale(activeLanguage: string): DayPickerProps["locale"] {
  if (activeLanguage.startsWith("es")) return es;
  if (activeLanguage.startsWith("pt")) return ptBR;
  return enUS;
}

// Override react-day-picker tokens to match the Coloca design system. The
// component otherwise inherits its colours from Tailwind utility classes
// applied via `classNames`.
export const DAY_PICKER_STYLE = {
  "--rdp-accent-color": "var(--primary)",
  "--rdp-accent-background-color": "var(--accent)",
  "--rdp-today-color": "var(--primary)",
  "--rdp-day-height": "2.25rem",
  "--rdp-day-width": "2.25rem",
  "--rdp-day_button-height": "2rem",
  "--rdp-day_button-width": "2rem",
  "--rdp-day_button-border-radius": "0.375rem",
  "--rdp-day_button-border": "2px solid transparent",
  "--rdp-selected-border": "none",
  "--rdp-nav_button-height": "2.5rem",
  "--rdp-nav_button-width": "2.5rem",
  "--rdp-nav-height": "2.5rem",
  "--rdp-dropdown-gap": "0.5rem",
  "--rdp-outside-opacity": "0.5",
  "--rdp-disabled-opacity": "0.35",
} as CSSProperties;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Parses a form value (`YYYY-MM-DD`) into a LOCAL date — never via `new Date(string)` (UTC shift). */
export function isoToDate(value: string | undefined | null): Date | undefined {
  if (!value || !ISO_DATE.test(value)) return undefined;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** Serializes a LOCAL date to the `YYYY-MM-DD` form value. */
export function dateToIso(date: Date): string {
  const y = date.getFullYear().toString().padStart(4, "0");
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const d = date.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Coloca theme classes shared by both calendars. Range fields layer `range_*` overrides on top. */
export const DAY_PICKER_CLASSNAMES = {
  root: "w-full text-sm text-foreground",
  // Caption and navigation share one grid row instead of react-day-picker's absolutely positioned `.rdp-nav`
  // (`style.css`: `position: absolute; inset-inline-end: 0`). That default assumes a caption as narrow as a
  // plain "noviembre 2026" label; the Coloca selects that replaced the native dropdowns are wider, and the
  // arrows ended up sitting on top of the year select. `1fr auto` gives the caption whatever it needs — any
  // locale's longest month name — and pins the arrows to the right of it, never over it.
  month: "grid grid-cols-[1fr_auto] items-center gap-2",
  month_caption: "flex min-w-0 items-center",
  // The month/year dropdowns are rendered by `DayPickerDropdown` (the `Dropdown` slot), which owns its own
  // markup and styling — hence no `dropdown`, `dropdown_root` or `caption_label` entry here. Both calendars
  // set `captionLayout="dropdown"`, so the caption is always that component.
  dropdowns: "flex min-w-0 items-center gap-2",
  nav: "flex items-center gap-1",
  weekday: "text-xs font-medium uppercase text-muted-foreground",
  button_previous:
    "inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50",
  button_next:
    "inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50",
  // Spans both grid columns so the day table keeps the full calendar width under the caption row.
  month_grid: "col-span-2 mt-3 w-full",
  day: "text-foreground",
  day_button:
    "inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-normal text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  selected:
    "[&_button]:bg-primary [&_button]:text-primary-foreground [&_button:hover]:bg-primary [&_button:hover]:text-primary-foreground",
  today: "[&_button]:font-semibold [&_button]:text-primary",
} as const;

/**
 * Trigger button styling shared by the date and date-range popover triggers. Mirrors the Input/SelectTrigger
 * primitives token-for-token (`border-input` + `dark:bg-input/30`, h-9, ring-3 focus) so the date field sits
 * visually flush with the other form controls — `bg-background` rendered noticeably darker in dark mode.
 */
export const DATE_TRIGGER_CLASS =
  "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-2.5 text-left text-sm text-foreground shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 dark:hover:bg-input/50";
